import { Geolocation, Position } from '@capacitor/geolocation'
import verifyPermission from './verifyPermission'
import getScreenType from '../device'
class GeolocationManager {
  private static instance: GeolocationManager
  private currentPosition: Position | null = null
  private watchId: string | null = null
  private subscribers = new Set<(position: Position | null) => void>()
  private isInitialized = false

  private constructor() {}

  static getInstance(): GeolocationManager {
    if (!GeolocationManager.instance) {
      GeolocationManager.instance = new GeolocationManager()
    }
    return GeolocationManager.instance
  }

  async initialize(): Promise<void> {
    if (this.isInitialized || getScreenType() === 'web') return
    
    try {
      await verifyPermission()
      await this.getCurrentPosition()
      await this.startWatching()
      this.isInitialized = true
    } catch (error) {
      console.error('GeolocationManager initialization failed:', error)
    }
  }

  private async getCurrentPosition(): Promise<void> {
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 5000
      })
      this.updatePosition(position)
    } catch (error) {
      console.error('Failed to get current position:', error)
    }
  }

  private async startWatching(): Promise<void> {
    if (this.watchId) return

    try {
      this.watchId = await Geolocation.watchPosition(
        {
          enableHighAccuracy: true,
          timeout: 30000,
          maximumAge: 5000
        },
        (position) => this.updatePosition(position)
      )
    } catch (error) {
      console.error('Failed to start watching position:', error)
    }
  }

  private updatePosition(position: Position | null): void {
    this.currentPosition = position
    this.subscribers.forEach(callback => callback(position))
  }

  getPosition(): Position | null {
    return this.currentPosition
  }

  getIsInitialized(): boolean {
    return this.isInitialized
  }

  subscribe(callback: (position: Position | null) => void): () => void {
    this.subscribers.add(callback)
    // Enviar posición actual inmediatamente si existe
    if (this.currentPosition) {
      callback(this.currentPosition)
    }
    
    // Retornar función de cleanup
    return () => {
      this.subscribers.delete(callback)
    }
  }

  async refreshPosition(): Promise<Position | null> {
    await this.getCurrentPosition()
    return this.currentPosition
  }

  destroy(): void {
    if (this.watchId) {
      Geolocation.clearWatch({ id: this.watchId })
      this.watchId = null
    }
    this.subscribers.clear()
    this.currentPosition = null
    this.isInitialized = false
  }
}

export default GeolocationManager